import { FabricObject, Canvas, PencilBrush } from "fabric";
import React, {
	CSSProperties,
	forwardRef,
	ReactNode,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { PanZoomApi, usePanZoom } from "./gestures";
import { useApi } from "./api";
import { Json } from "../model";
import {
	backgroundBlurStyles,
	colorPickerButtonActiveStyles,
	colorPickerButtonStyles,
	colorPickerStyles,
	colorPickerWrapperStyles,
	debugToolbarStyles,
	infoPillStyles,
	minimapViewportStyles,
	minmapStyles,
	pickerBoxStyles,
	pickerRangeWrapper,
	previewWindowStyles,
	rangeStyles,
	toolbarSpacerStylesVertical,
	toolbarStyles,
	transformTargetStyles,
	utilityBarStyles,
	widgetTargetStyles,
	wrapperStyles,
} from "./styles.css.ts";
import {
	colors,
	MIN_STROKE,
	MAX_STROKE,
	SELECTION_COLOR_TRANSPARENT,
	SELECTION_COLOR,
	initialX,
	initialY,
	initialScale,
	WIDTH,
	HEIGHT,
	SELECTION_COLOR_TRANSPARENT_LESS,
	SELECTION_BORDER_SIZE,
} from "../constants";

export const ReactApp = () => {
	console.log(`💦 location`, window.location.pathname);
	const search = new URLSearchParams(location.search);
	if (window.location.pathname === '/login') {
		return <Login />
	}

	const cookie = getCookie('auth_token');
	if (!cookie) {
		console.log(`🌈 no token cookie found, redirecting to /login in browser`);
		location.assign(`/login?board_id=${search.get('board_id')}`);
		return null;
	}

	return <Board />
}

function getCookie(name: string) {
  return document.cookie
    .split('; ')
    .find(row => row.startsWith(name + '='))
    ?.split('=')[1];
}

export const Login = () => {
	const search = new URLSearchParams(location.search);

	useEffect(() => {
		const cookie = getCookie('auth_token');
		if (cookie) {
			const [userId] = cookie.split(':');
			if (!search.get('user_id')) {
				search.set('user_id', userId);
			}
			location.assign(`/?${search}`);
		}
	}, []);

	return (
		<form id="login" action="/api/login" method="POST" style={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			fontSize: '3rem',
			padding: '2rem',
			width: '100%',
			height: '100%',
		}}
		onSubmit={async e => {
			e.preventDefault();

			const formData = new FormData(e.target as HTMLFormElement);
			const res = await fetch(`/api/login?${search}`, {
				method: "POST",
				body: formData,
			});
			console.log(`✅ submitted`, res.status, await res.text());
			if (res.status === 200) {
				document.cookie = `auth_token=${formData.get('username')}:${formData.get('password')}`
				search.set('user_id', formData.get('username') as string);
				location.assign(`/?${search}`);
			}
		}}
		>
		<div style={{
			display: 'flex',
			flexDirection: 'column',
			gap: '2rem',
		}}>
			<div style={rowStyle}>
				<label htmlFor="username">Username</label>
				<input type="text" id="username" name="username" autoComplete="username" style={inputStyle} />
			</div>
			<div style={rowStyle}>
				<label htmlFor="password">Password</label>
				<input type="password" id="password" name="password" autoComplete="current-password" style={inputStyle} />
			</div>
			<div>
				<input type="submit" style={{
					appearance: 'none',
					margin: 0,
					padding: '1rem',
					background: 'white',
					color: 'black',
					fontSize: 'inherit',
				}} />
			</div>
			</div>
		</form>
	);
}

export const Board = () => {
	const gestureRef = useRef<HTMLDivElement | null>(null);
	const transformRef = useRef<HTMLDivElement | null>(null);
	const fabricRef = useRef<FabricRef | null>(null);
	const [tool, setTool] = useState<Tool>("pen");
	const [pz, setPz] = useState<PanZoomApi | null>(null);
	const [{ x, y, scale }, setDims] = useState({
		x: initialX,
		y: initialY,
		scale: initialScale,
	});
	const [hasSelection, setHasSelection] = useState(false);
	const [isHydrating, setHydrating] = useState(true);
	const [activeColor, setActiveColor] = useState(colors[7]);
	const [stroke, setStroke] = useState(30);
	const [pickViz, setPickViz] = useState<PickViz>("none");
	const [{ can_undo, can_redo }, setHistory] = useState({
		can_undo: false,
		can_redo: false,
	});
	const [showLogout, setShowLogout] = useState(false);

	const api = useApi({
		onHydrateChange: (h) => {
			setHydrating(h);
		},
		loadFromJson: async (json: Json) => {
			console.log(`canvas.loadFromJson`, json);
			await fabricRef.current?.loadFromJSON(json);
		},
		onHistoryChange: setHistory,
	});

	useEffect(() => {
		const search = new URLSearchParams(location.search);
		const cookie = getCookie('auth_token');
		if (cookie) {
			const [userId] = cookie.split(':');
			if (!search.get('user_id')) {
				search.set('user_id', userId);
				location.assign(`/?${search}`);
			}
		} else {
			console.log(`🌈🌈🌈🌈🌈 redirect for missing cooki`, { search });
			location.assign(`/login?${search}`);
		}
	}, []);

	useEffect(
		function syncFabricTool() {
			if (tool === "move") {
				pz?.enable();
			} else {
				pz?.disable();
			}
			fabricRef.current?.setTool(tool);
			fabricRef.current?.setPenProps(activeColor, stroke);
			document.documentElement.style.setProperty("--theme-color", activeColor);
		},
		[tool, pz, activeColor, stroke],
	);

	useEffect(() => {
		if (!gestureRef.current || !transformRef.current) return;

		const pz = usePanZoom({
			initialScale,
			initialX,
			initialY,
			wrapperEl: gestureRef.current,
			transformEl: transformRef.current,
			onChange: (x, y, scale) => {
				setDims({ x, y, scale });
			},
		});
		setPz(pz);
	}, []);

	useEffect(() => {
		setFabricSelectionStyles();
	}, []);

	return (
		<div
			style={{
				display: "relative",
			}}
		>
			<div className={cn(infoPillStyles, backgroundBlurStyles)} onClick={() => setShowLogout(!showLogout)}>
				{isHydrating ? `Connecting...` : `${(scale * 100).toFixed(0)}%`}
			</div>
			{showLogout && <button onClick={(e) => {
				e.preventDefault();
				api.logout()
			}} className={backgroundBlurStyles} style={{
				appearance: 'none',
				position: 'fixed',
				top: 140,
				right: 40,
				fontSize: '2.5rem',
				padding: '2rem 4rem',
				zIndex: 200,
				color: 'white',
				borderRadius: '4rem',
			}}>Logout</button>}
			<div
				style={{
					// Fade content while hydrating to indicate the app is not interactive
					position: "relative",
					opacity: isHydrating ? "0.25" : "1",
					pointerEvents: isHydrating ? "none" : undefined,
				}}
			>
				<div ref={gestureRef} className={cn(wrapperStyles)}>
					<div
						ref={transformRef}
						className={cn(transformTargetStyles, scale > 1.5 && "zoomed")}
					>
						<div className={widgetTargetStyles} />
						<FabricCanvas
							ref={fabricRef}
							onSelect={setHasSelection}
							scheduleSync={api.scheduleSync}
							color={activeColor}
						/>
					</div>
				</div>
				<MiniMap x={x} y={y} scale={scale} />
				{tool === "pen" && pickViz !== "none" ? (
					<StrokePicker
						stroke={stroke}
						color={activeColor}
						onChange={({ stroke, color }) => {
							setStroke(stroke);
							setActiveColor(color);
						}}
						scale={scale}
						close={() => setPickViz("none")}
					/>
				) : (
					<UtilityBar
						canUndo={can_undo}
						canRedo={can_redo}
						undo={() => {
							api.undo();
						}}
						redo={() => {
							api.redo();
						}}
					/>
				)}
				<PrimaryToolbar
					activeColor={activeColor}
					fabricRef={fabricRef}
					tool={tool}
					pickViz={pickViz}
					setPickViz={setPickViz}
					setTool={setTool}
				/>
				<SecondaryToolbar
					stroke={stroke}
					activeColor={activeColor}
					fabricRef={fabricRef}
					tool={tool}
					pickViz={pickViz}
					setPickViz={setPickViz}
					hasSelection={hasSelection}
				/>
			</div>
		</div>
	);
};

function cn(...classNames: Array<boolean | undefined | string>) {
	return classNames.filter((n) => typeof n === "string").join(" ");
}

function setFabricSelectionStyles() {
	const borderScaleFactor = SELECTION_BORDER_SIZE;
	FabricObject.ownDefaults.borderColor = SELECTION_COLOR_TRANSPARENT;
	FabricObject.ownDefaults.borderScaleFactor = borderScaleFactor;
	FabricObject.ownDefaults.borderOpacityWhenMoving = 0.6;
	FabricObject.ownDefaults.borderDashArray = [
		borderScaleFactor,
		borderScaleFactor * 2,
	];

	FabricObject.ownDefaults.cornerColor = SELECTION_COLOR;
	FabricObject.ownDefaults.cornerStrokeColor = SELECTION_COLOR;
	FabricObject.ownDefaults.cornerSize = borderScaleFactor * 2;
	FabricObject.ownDefaults.cornerStyle = "rect";

	FabricObject.ownDefaults.transparentCorners = false;
	FabricObject.ownDefaults.padding = 0;
	FabricObject.ownDefaults.hasBorders = true;

	// FabricObject.controls.mtr = new controlsUtils.RotationControl({
	//   offsetY: -30,
	//   sizeX: 12,
	//   sizeY: 12,
	//   cornerColor: "#4f46e5",
	//   cornerStrokeColor: "#ffffff",
	// });
}

type PickViz = "none" | "color" | "stroke";

const Toolbar = forwardRef<
	HTMLDivElement,
	{
		className?: string;
		children: ReactNode;
		debug?: boolean;
		style?: React.CSSProperties;
	}
>((props, ref) => {
	return (
		<div
			ref={ref}
			className={cn(
				props.debug && debugToolbarStyles,
				toolbarStyles,
				props.className,
				backgroundBlurStyles,
			)}
			style={props.style}
		>
			{props.children}
		</div>
	);
});

const PrimaryToolbar = (props: {
	tool: Tool;
	setTool(tool: Tool): void;
	pickViz: PickViz;
	activeColor: string;
	fabricRef: React.RefObject<FabricRef | null>;
	setPickViz(value: PickViz): void;
}) => {
	return (
		<Toolbar
			style={{
				bottom: 100,
			}}
		>
			<ToolbarButton
				isFirst
				id="clear"
				onClick={() => props.fabricRef.current?.clear()}
			>
				<ClearIcon
					width={64}
					height={64}
					style={{ transform: "translateY(1px)" }}
				/>
			</ToolbarButton>
			<ToolbarButton
				id="move"
				active={props.tool === "move"}
				onClick={() => props.setTool("move")}
			>
				<MoveIcon
					width={70}
					height={70}
					style={{ transform: "translateY(0px)" }}
				/>
			</ToolbarButton>
			<ToolbarButton
				id="select"
				active={props.tool === "select"}
				onClick={() => props.setTool("select")}
				cx={16}
			>
				<SelectIcon
					width={74}
					height={74}
					style={{ transform: "translateY(-10px)" }}
				/>
			</ToolbarButton>
			<ToolbarButton
				isLast
				id="pen"
				active={props.tool === "pen"}
				onClick={(e) => {
					e.preventDefault();
					if (props.tool === "pen") {
						props.setPickViz(props.pickViz === "none" ? "stroke" : "none");
						return;
					}

					props.setTool("pen");
					props.setPickViz("none");
				}}
				cx={-12}
			>
				<PenIcon style={{ transform: "translateY(1px)" }} />
			</ToolbarButton>
		</Toolbar>
	);
};
const SecondaryToolbar = (props: {
	stroke: number;
	tool: Tool;
	pickViz: PickViz;
	activeColor: string;
	hasSelection: boolean;
	fabricRef: React.RefObject<FabricRef | null>;
	setPickViz(value: PickViz): void;
}) => {
	const outMin = 4;
	const outMax = 50;
	const strokeScaled = mapRange(
		props.stroke,
		MIN_STROKE,
		MAX_STROKE,
		outMin,
		outMax,
	);

	return (
		<Toolbar
			style={{
				bottom: 280,
				transformOrigin: "right bottom",
			}}
		>
			{props.tool === "pen" && props.pickViz === "none" && (
				<>
					<ToolbarButton secondary isOnly>
						<div
							className="active-selection"
							onClick={() => {
								props.setPickViz("stroke");
							}}
							style={{
								width: outMax + 10,
								height: outMax + 10,
								position: "relative",
								zIndex: 101,
								borderRadius: outMax + 10,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								border: "3px dashed rgba(255, 255, 255, 0.4)",
							}}
						>
							<div
								style={{
									background: props.activeColor,
									width: strokeScaled,
									height: strokeScaled,
									borderRadius: strokeScaled,
								}}
							/>
						</div>
					</ToolbarButton>
				</>
			)}
			{props.tool === "select" && (
				<ToolbarButton
					isOnly
					secondary
					id="delete"
					onClick={() => props.fabricRef.current?.deleteSelected()}
					disabled={!(props.tool === "select" && props.hasSelection)}
				>
					<DeleteIcon
						width={50}
						height={50}
						style={{ transform: "translateY(0px)" }}
					/>
				</ToolbarButton>
			)}
		</Toolbar>
	);
};

const ToolbarButton = ({
	id,
	cx = 0,
	disabled = false,
	active = false,
	children,
	onClick,
	secondary = false,
	isFirst = false,
	isLast = false,
	isOnly = false,
}: {
	id?: string;
	active?: boolean;
	children: ReactNode;
	onClick?(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
	disabled?: boolean;
	secondary?: boolean;
	cx?: number;
	isFirst?: boolean;
	isLast?: boolean;
	isOnly?: boolean;
}) => {
	return (
		<div className="toolbar-button-wrap" style={{ position: "relative" }}>
			<button
				id={id}
				onClick={onClick}
				className="toolbar-button"
				disabled={disabled}
				style={{
					//@TODO:
					background: "transparent",
					appearance: "none",
					border: 0,
					margin: 0,
					padding: "2.5rem 1.75rem",
					outline: "none",
					color: "#FFFFFF",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "6rem",
					minWidth: "6rem",
					flex: 1,
					opacity: disabled ? 0.15 : active ? 1 : secondary ? 1 : 0.4,
					paddingLeft: isFirst || isOnly ? "2.75rem" : "1.75rem",
					paddingRight: isLast || isOnly ? "2.75rem" : "1.75rem",
				}}
			>
				{children}
			</button>
			{active && (
				<div
					style={{
						position: "absolute",
						left: 0,
						right: 0,
						margin: "auto",
						width: 14,
						height: 14,
						borderRadius: 10,
						background: "white",
						bottom: 10,
						transform: `translateX(${cx}px)`,
					}}
				/>
			)}
		</div>
	);
};

type Tool = "pen" | "select" | "move";
type FabricRef = {
	setTool(tool: Tool): void;
	clear(): void;
	deleteSelected(): void;
	loadFromJSON(json: Json): Promise<void>;
	setPenProps(color: string, stroke: number): void;
};

const FabricCanvas = forwardRef<
	FabricRef,
	{
		onSelect?(hasSelection: boolean): void;
		scheduleSync?(reason: string, jsonContent: Json, svg: string): void;
		color: string;
	}
>(({ onSelect, scheduleSync, color }, ref) => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	let canvas: Canvas | null = null;
	let pen: PencilBrush | null = null;
	let initialTool: Tool | null = null;

	function setTool(c: Canvas, tool: Tool) {
		if (!c) return;

		const r = c.discardActiveObject();
		c.requestRenderAll();
		c.selection = tool === "select";
		c.skipTargetFind = tool !== "select";
		c.isDrawingMode = tool === "pen";
		if (tool === "pen") {
			if (pen) {
				c.freeDrawingBrush = pen;
			} else {
				console.log(`❌ missing pen`);
			}
		}
	}

	useEffect(function initFabric() {
		if (!canvasRef.current) return;

		const c = new Canvas(canvasRef.current, {
			isDrawingMode: false,
			selection: true,
			width: WIDTH,
			height: HEIGHT,
			selectionColor: SELECTION_COLOR_TRANSPARENT_LESS,
			selectionBorderColor: SELECTION_COLOR,
			selectionLineWidth: 4,
		});
		canvas = c;

		if (initialTool) {
			setTool(c, initialTool);
		}

		const boardUpdated = () => {
			if (!c) {
				console.log(`❌ boardUpdated missing canvas`);
				return;
			}
			const s = c.toSVG({
				width: `${WIDTH}px`,
				height: `${HEIGHT}px`,
				viewBox: { x: 0, y: 0, width: WIDTH, height: HEIGHT },
			});
			const svgContent = s.replace(
				/\<svg\s/,
				`<svg style="background-color: #000" `,
			);

			scheduleSync?.("boardUpdated", c.toJSON(), svgContent);
		};

		c.on("path:created", boardUpdated);
		c.on("object:modified", boardUpdated);
		c.on("object:removed", boardUpdated);
		c.on("object:added", boardUpdated);

		c.on("selection:created", (e) => {
			onSelect?.(true);
		});

		c.on("selection:updated", (e) => {
			onSelect?.(!!e.selected);
		});

		c.on("selection:cleared", (e) => {
			onSelect?.(false);
		});

		const p = new PencilBrush(c);
		p.width = 4;
		p.color = color;
		pen = p;

		return function disposeFabric() {
			c.dispose();
			canvas = null;
			pen = null;
		};
	}, []);

	useImperativeHandle(
		ref,
		() => ({
			setTool: (tool) => {
				if (!canvas) {
					console.log(
						`❌ setTool(${tool}) missing canvas, setting initialTool`,
					);
					initialTool = tool;
					return;
				}
				setTool(canvas, tool);
			},

			clear: () => {
				if (!canvas) throw new Error("missing canvas");

				canvas.clear();
			},

			deleteSelected: () => {
				if (!canvas) {
					console.log(`❌ deleteSelected no canvas`);
					return;
				}
				const activeOjbs = canvas.getActiveObjects();

				activeOjbs.forEach((obj) => {
					canvas?.remove(obj);
				});

				canvas.discardActiveObject();
				canvas.requestRenderAll();
			},

			loadFromJSON: async (json: Json) => {
				if (!canvas) {
					console.log(`❌ loadFromJSON no canvas`);
					return;
				}
				await canvas.loadFromJSON(json as string);
				canvas.requestRenderAll();
			},

			setPenProps: (c, s) => {
				if (pen) {
					pen.color = c;
					pen.width = s;
				}
			},
		}),
		[canvas],
	);

	return <canvas ref={canvasRef} id="board" />;
});

type IconProps = {
	width?: CSSProperties["width"];
	height?: CSSProperties["height"];
	style?: CSSProperties;
	viewBox?: string;
	children?: ReactNode;
	fill?: string;
};

const Icon = ({
	children,
	width = 60,
	height = 60,
	fill = "white",
	...props
}: IconProps) => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			version="1.1"
			width={width}
			height={height}
			fill={fill}
			{...props}
		>
			{children}
		</svg>
	);
};

const ScribbleIcon = ({
	thick = false,
	...props
}: IconProps & { thick?: boolean }) => {
	return (
		<Icon {...props} viewBox="11.486486486486484 14.57 77.02702702702702 71.28">
			<path
				strokeWidth={thick ? 10 : 0}
				stroke={props.fill || "white"}
				d="m16.77 56.309c0.21094-0.21875 21.16-21.82 32.922-31.07 6.1719-4.8516 10.34-6.2812 12.391-4.2305 3.7617 3.75-3.7188 13.691-12.379 25.211l-0.089844 0.12109c-1.2891 1.7109-2.5312 3.3281-3.7109 4.8594-8.3594 10.891-14.391 18.762-7.9102 24.871 1.4609 1.3789 3.3398 2.0703 5.4609 1.9805 6.2617-0.26172 13.859-7.3398 19.141-13.23 6.4219-7.1719 11.078-10.859 13.859-10.988 0.62891-0.019531 0.91016 0.17188 1.1406 0.37891 2.1914 2.0391-1.8984 9.3984-3.6484 12.539-0.41016 0.73047-0.78125 1.3984-1.0781 1.9688l-0.51172 0.96094c-2.1289 4.0117-5.0391 9.5117-0.64062 13.488 1.2695 1.1484 2.8203 1.75 4.5117 1.75 0.17969 0 0.35156 0 0.53125-0.019531 5-0.37109 9.6992-5.9102 10.219-6.5391 0.87891-1.0703 0.71875-2.6406-0.33984-3.5195-1.0703-0.87891-2.6406-0.73047-3.5195 0.33984-1.4492 1.75-4.5508 4.5781-6.7305 4.7383-0.51953 0.039062-0.92188-0.10156-1.3086-0.44922-1.3008-1.1797-0.57031-3.1602 1.6992-7.4414l0.51953-0.98828c0.28125-0.53906 0.62891-1.1602 1.0117-1.8516 2.8984-5.2109 7.75-13.922 2.6914-18.629-1.2891-1.2109-2.9297-1.8086-4.7812-1.7188-5.5703 0.25-12.199 6.8906-17.352 12.648-6.2617 7-12.25 11.43-15.621 11.57-0.76172 0.039062-1.3281-0.16016-1.8086-0.62109-3.0586-2.8906 0.57812-7.9414 8.4492-18.191 1.1914-1.5391 2.4414-3.1797 3.7383-4.8984l0.089844-0.12109c10.891-14.48 18.762-24.941 11.91-31.75-6.2188-6.2109-15.852 1.3516-19.012 3.8398-12.02 9.4609-32.551 30.621-33.422 31.52-0.96094 0.98828-0.94141 2.5703 0.058594 3.5391 0.98828 0.96094 2.5781 0.94141 3.5391-0.058594z"
			/>
		</Icon>
	);
};

const ClearIcon = ({
	thick = false,
	...props
}: IconProps & { thick?: boolean }) => {
	return (
		<Icon {...props} viewBox="15.5 9 69.25 81.75">
			<path
				d="M 44.3512,55.2697L 50.9803,61.8988L 39.9318,72.9473L 50.9803,83.9959L 44.3512,90.625L 33.3027,79.5764L 22.2541,90.625L 15.625,83.9958L 26.6735,72.9473L 15.625,61.8988L 22.2541,55.2697L 33.3027,66.3182L 44.3512,55.2697 Z"
				strokeLinejoin="round"
				strokeWidth="0.2"
			/>
			<path
				d="M 59.375,34.375L 76.5624,34.375L 59.375,17.1876L 59.375,34.375 Z M 78.125,40.625L 53.125,40.625L 53.125,15.625L 21.875,15.625L 21.875,50L 15.625,56.25L 15.625,9.37503L 60.9375,9.37503L 84.375,32.8125L 84.375,90.625L 49.9999,90.6251L 56.2498,84.3752L 78.125,84.375L 78.125,40.625 Z"
				strokeLinejoin="round"
				strokeWidth="0.2"
			/>
		</Icon>
	);
};

const SelectIcon = (props: IconProps) => {
	return (
		<Icon
			{...props}
			viewBox="1.756756756756757 2.1500000000000004 96.21621621621622 96.11999999999999"
		>
			<path d="m97.223 53.695-62.043-18.512 18.516 62.039 11.32-20.863 15.883 15.887 11.348-11.348-15.887-15.883z" />
			<path d="m11.023 13.934 2.9141-2.9141 12.09 12.09-2.9141 2.9141z" />
			<path d="m2.7773 33.84h17.094v4.1172h-17.094z" />
			<path d="m11.023 57.863 12.09-12.09 2.9141 2.9141-12.09 12.09z" />
			<path d="m45.777 23.109 12.09-12.09 2.9141 2.9141-12.09 12.09z" />
			<path d="m33.84 2.7773h4.1172v17.094h-4.1172z" />
		</Icon>
	);
};

const MoveIcon = (props: IconProps) => {
	return (
		<Icon {...props} viewBox="3.378378378378379 3.5 93.24324324324324 93.69">
			<g>
				<path d="m94.707 47.293-10.75-10.75c-1.4844-1.4844-3.8906-1.4844-5.375 0s-1.4844 3.8906 0 5.375l4.168 4.168-21.457-0.003907c-2.1055 0-3.8125 1.707-3.8125 3.8125s1.707 3.8125 3.8125 3.8125h21.543l-4.168 4.168c-1.4844 1.4844-1.4844 3.8906 0 5.375s3.8906 1.4844 5.375 0l10.793-10.793h-0.003907c1.3203-1.4883 1.2656-3.7422-0.125-5.1641z" />
				<path d="m17.168 53.793h21.539c2.1055 0 3.8125-1.707 3.8125-3.8125s-1.707-3.8125-3.8125-3.8125h-21.539l4.168-4.168h-0.003907c1.5078-1.4609 1.5469-3.8672 0.085938-5.375-1.4609-1.5078-3.8672-1.543-5.375-0.082031l-10.75 10.75c-0.71484 0.71094-1.1172 1.6758-1.1172 2.6875 0 1.0078 0.40234 1.9766 1.1172 2.6875l10.793 10.793-0.003907-0.003907c1.4844 1.4844 3.8906 1.4844 5.375 0s1.4844-3.8906 0-5.375z" />
				<path d="m41.918 21.457 4.168-4.168-0.003907 21.418c0 2.1055 1.707 3.8125 3.8125 3.8125s3.8125-1.707 3.8125-3.8125v-21.539l4.168 4.168v-0.003907c1.4844 1.4844 3.8906 1.4844 5.375 0s1.4844-3.8906 0-5.375l-10.543-10.664c-0.71094-0.71484-1.6758-1.1172-2.6875-1.1172-1.0078 0-1.9766 0.40234-2.6875 1.1172l-10.789 10.75c-1.4844 1.4844-1.4844 3.8906 0 5.375s3.8906 1.4844 5.375 0z" />
				<path d="m58.082 78.543-4.168 4.168 0.003907-21.418c0-2.1055-1.707-3.8125-3.8125-3.8125s-3.8125 1.707-3.8125 3.8125v21.543l-4.168-4.168c-1.4844-1.4844-3.8906-1.4844-5.375 0s-1.4844 3.8906 0 5.375l10.793 10.793v-0.003907c0.71094 0.71484 1.6758 1.1172 2.6875 1.1172 1.0078 0 1.9766-0.40234 2.6875-1.1172l10.793-10.793-0.003907 0.003907c1.4844-1.4844 1.4844-3.8906 0-5.375s-3.8906-1.4844-5.375 0z" />
			</g>
		</Icon>
	);
};

const DeleteIcon = (props: IconProps) => {
	return (
		<Icon
			{...props}
			viewBox="7.702702702702702 6.469999999999999 84.86486486486487 87.21000000000001"
		>
			<path d="m87.684 19.75h-16.316v-0.39844c-0.007813-6.8672-5.5703-12.426-12.434-12.434h-17.867c-6.8633 0.007812-12.426 5.5664-12.434 12.434v0.39844h-16.316c-2.2109 0-4 1.7891-4 4s1.7891 4 4 4h4.2578l3.2695 49.324c0.29688 4.3359 2.2266 8.3984 5.4023 11.371 3.1758 2.9688 7.3555 4.6289 11.703 4.6367h12.367c0.23047 0 0.45703-0.023437 0.68359-0.066406 0.22656 0.042969 0.45312 0.066406 0.68359 0.066406h12.367c4.3477-0.007812 8.5273-1.668 11.703-4.6367 3.1758-2.9727 5.1055-7.0352 5.4023-11.371l3.2695-49.324h4.2578c2.2109 0 4-1.7891 4-4s-1.7891-4-4-4zm-51.051-0.39844c0.003907-2.4492 1.9883-4.4336 4.4336-4.4336h17.867c2.4453 0 4.4297 1.9844 4.4336 4.4336v0.39844h-26.734zm35.539 57.195 0.003906-0.003906c-0.33203 4.8008-4.3164 8.5273-9.125 8.5391h-12.367c-0.23047 0.003907-0.45703 0.027344-0.68359 0.070313-0.22656-0.042969-0.45312-0.066406-0.68359-0.070313h-12.367c-4.8086-0.011719-8.793-3.7383-9.125-8.5391l-3.2344-48.793h50.82z" />
			<path d="m36 42.418c-2.2109 0-4 1.7891-4 4v19.332c0 2.2109 1.7891 4 4 4s4-1.7891 4-4v-19.332c0-2.2109-1.7891-4-4-4z" />
			<path d="m50 42.418c-2.2109 0-4 1.7891-4 4v19.332c0 2.2109 1.7891 4 4 4s4-1.7891 4-4v-19.332c0-2.2109-1.7891-4-4-4z" />
			<path d="m64 42.418c-2.2109 0-4 1.7891-4 4v19.332c0 2.2109 1.7891 4 4 4s4-1.7891 4-4v-19.332c0-2.2109-1.7891-4-4-4z" />
		</Icon>
	);
};

const PenIcon = (props: IconProps) => {
	return (
		<Icon
			width={56}
			height={56}
			{...props}
			viewBox="14.18918918918919 12.14 72.16216216216216 76.13999999999999"
		>
			<path
				d="m77.09 17.289c-1.6094-1.6094-4.2812-1.6094-5.8906 0l-0.375 0.375 13.262 13.254 0.37109-0.37109c1.6094-1.6094 1.6094-4.2812 0-5.8906zm5.1523 17.676c-7.043-7.0391-14.09-14.074-21.129-21.113-1.6602-1.6602-4.418-1.6602-6.0781 0l-18.023 18.02c-0.8125 0.8125-0.8125 2.1328 0 2.9453 0.8125 0.81641 2.1367 0.81641 2.9492 0l18.02-18.02c0.070312-0.070313 0.11719-0.078125 0.19141 0l2.707 2.707-35.684 35.684c-0.30078 0.30078-0.67187 0.66797-1.0625 1.0703l21.352 21.355 36.758-36.758c1.6094-1.6094 1.6094-4.2812 0-5.8906zm-60.758 24.543c-3.6602 5.707-4.4766 13.285-2.6992 19.363 0.085938 0.28906 0.19141 0.55859 0.3125 0.8125l-4.1523 4.1523c-0.8125 0.8125-0.8125 2.1328 0 2.9453s2.1328 0.8125 2.9453 0l4.1523-4.1523c0.25781 0.125 0.52734 0.23438 0.82031 0.32031 6.0781 1.7773 13.66 0.96094 19.367-2.6953z"
				fillRule="evenodd"
			/>
		</Icon>
	);
};

const UndoIcon = (props: IconProps) => {
	return (
		<Icon width={40} height={40} {...props} viewBox="0 0 57 60">
			<path
				fill-rule="evenodd"
				clip-rule="evenodd"
				d="M29.4189 7.70161C37.1356 8.40475 44.009 12.061 48.9419 17.5428C53.8308 22.978 56.8129 30.2154 56.8129 38.154C56.8129 41.8314 56.1689 45.366 54.9894 48.6402C53.7683 52.0387 51.9702 55.1532 49.7173 57.8628C49.0549 58.6597 48.1708 59.1565 47.2244 59.3253C46.2826 59.4941 45.2832 59.3347 44.3922 58.8167C43.5012 58.2988 42.8595 57.5019 42.5248 56.5831C42.1924 55.6714 42.1693 54.6471 42.5132 53.6651C42.8618 52.6737 43.1273 51.6425 43.3073 50.5784C43.4804 49.5471 43.5728 48.4808 43.5728 47.3839C43.5728 43.1042 42.1624 39.1597 39.7919 36.0073C37.3659 32.78 33.9266 30.3776 29.9544 29.2831C29.7121 29.2175 29.4789 29.2034 29.2642 29.2479C29.0473 29.2901 28.8349 29.3909 28.6387 29.5456C28.4402 29.6979 28.2902 29.8807 28.1932 30.0776C28.0986 30.2745 28.0478 30.5042 28.0478 30.7643V31.3573C28.0478 32.1588 27.8401 32.9112 27.4523 33.5651C27.0668 34.2167 26.5082 34.7558 25.8088 35.1354C25.1094 35.5151 24.357 35.6839 23.616 35.6464C22.8659 35.6089 22.1342 35.3628 21.474 34.9128L2.05968 21.687C1.46416 21.2815 0.997875 20.7588 0.67707 20.1495C0.35392 19.5331 0.183105 18.8487 0.183105 18.1291C0.183105 17.4073 0.353914 16.7229 0.67707 16.1088C0.997916 15.4995 1.46416 14.9768 2.05968 14.5713L21.474 1.34551C22.1342 0.897857 22.8659 0.651791 23.616 0.611891C24.3593 0.574391 25.1094 0.743141 25.8088 1.12283C26.5082 1.50251 27.0668 2.04155 27.4523 2.69315C27.8401 3.34943 28.0478 4.10177 28.0478 4.90097V6.16193C28.0478 6.56974 28.1955 6.93305 28.4356 7.20257C28.6826 7.47679 29.0265 7.6596 29.4166 7.69475L29.4189 7.70161Z"
			/>
		</Icon>
	);
};

const RedoIcon = (props: IconProps) => {
	return (
		<div style={{ transform: "rotateY(180deg)" }}>
			<UndoIcon {...props} />
		</div>
	);
};

const UtilityBar = (props: {
	canUndo: boolean;
	canRedo: boolean;
	undo(): void;
	redo(): void;
}) => {
	return (
		<Toolbar className={utilityBarStyles}>
			<ToolbarButton secondary disabled={!props.canUndo} onClick={props.undo}>
				<div style={{ padding: `1rem` }}>
					<UndoIcon />
				</div>
			</ToolbarButton>
			<div className={toolbarSpacerStylesVertical} />
			<ToolbarButton secondary disabled={!props.canRedo} onClick={props.redo}>
				<div style={{ padding: `1rem` }}>
					<RedoIcon />
				</div>
			</ToolbarButton>
		</Toolbar>
	);
};

const StrokePicker = ({
	stroke,
	color,
	onChange,
	scale,
	close,
}: {
	scale: number;
	color: string;
	stroke: number;
	onChange(value: { stroke: number; color: string }): void;
	close(): void;
}) => {
	const ref = useRef<HTMLInputElement | null>(null);
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!ref.current) return;

		ref.current.style.setProperty(
			"--val",
			`${((stroke - MIN_STROKE) / (MAX_STROKE - MIN_STROKE)) * 100}%`,
		);
	}, [stroke]);

	useEffect(function closeOnClickOutside() {
		function listener(e: PointerEvent) {
			if (!wrapperRef.current) {
				return;
			}

			if (e.target && !wrapperRef.current.contains(e.target as Node)) {
				close();
			}
		}

		document.addEventListener("pointerdown", listener, true);

		return () => {
			document.removeEventListener("pointerdown", listener, true);
		};
	}, []);

	return (
		<Toolbar className={pickerBoxStyles} ref={wrapperRef}>
			<div className={colorPickerWrapperStyles}>
				<div className={previewWindowStyles}>
					<div
						style={{
							width: `calc(${MAX_STROKE}px + 1rem)`,
							height: `calc(${MAX_STROKE}px + 1rem)`,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							color: "black",
						}}
					>
						<div
							style={{
								width: stroke,
								height: stroke,
								borderRadius: stroke,
								background: "var(--theme-color)",
								border: "2px solid rgba(255, 255, 255,  0.25)",
								transform: `scale(${scale}`,
							}}
						/>
					</div>
				</div>
				<div className={colorPickerStyles}>
					{colors.map((c) => {
						return (
							<button
								key={c}
								className={`${colorPickerButtonStyles} ${c === color ? colorPickerButtonActiveStyles : ""}`}
								style={{ background: c }}
								onClick={() => {
									onChange({ stroke, color: c });
								}}
							></button>
						);
					})}
				</div>
			</div>
			<div className={pickerRangeWrapper}>
				<ScribbleIcon
					fill="rgba(255, 255, 255, 0.75)"
					width={36}
					height={36}
					style={{ transform: "translateY(-2px)" }}
				/>
				<input
					ref={ref}
					type="range"
					min={MIN_STROKE}
					max={MAX_STROKE}
					value={stroke}
					className={rangeStyles}
					style={{
						flex: 1,
					}}
					onChange={(e) => {
						onChange({ stroke: parseInt(e.target.value), color });
					}}
				/>
				<ScribbleIcon
					fill="rgba(255, 255, 255, 0.75)"
					thick
					width={36}
					height={36}
					style={{ transform: "translateY(-2px)" }}
				/>
			</div>
		</Toolbar>
	);
};

function mapRange(
	value: number,
	inMin: number,
	inMax: number,
	outMin: number,
	outMax: number,
): number {
	return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}

const MAX_MINIMAP_WIDTH = 100;
const MiniMap = (props: { x: number; y: number; scale: number }) => {
	const [size, setSize] = useState({ width: 0, height: 0 });

	const canvasSize = { w: WIDTH * props.scale, h: HEIGHT * props.scale };
	const viewportSize = { w: size.width, h: size.height, x: 0, y: 0 };
	const maxWidth = Math.max(canvasSize.w, viewportSize.w);
	const scaleFactor = MAX_MINIMAP_WIDTH / maxWidth;

	canvasSize.w = canvasSize.w * scaleFactor;
	canvasSize.h = canvasSize.h * scaleFactor;
	viewportSize.w = viewportSize.w * scaleFactor;
	viewportSize.h = viewportSize.h * scaleFactor;
	viewportSize.x = props.x * scaleFactor;
	viewportSize.y = props.y * scaleFactor;

	useLayoutEffect(() => {
		const rect = document.body.getBoundingClientRect();
		setSize(rect);
	}, []);

	return (
		<div
			className={minmapStyles}
			style={{
				width: canvasSize.w,
				height: canvasSize.h,
				opacity: props.scale > 1 ? 1 : 0,
			}}
		>
			<div
				className={minimapViewportStyles}
				style={{
					width: viewportSize.w, height: viewportSize.h, transform: `translate(${viewportSize.x * -1}px, ${viewportSize.y * -1}px)`,
				}}
			/>
		</div>
	);
};


const rowStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '1.5rem',
}

const inputStyle: CSSProperties = {
	minHeight: '3rem',
	fontSize: 'inherit',
	appearance: 'none',
	padding: '0.5rem 1rem',
	border: 'none',
	background: 'rgba(255, 255, 255, 0.75)',
}
